import * as Yup from 'yup';

export type FilingScheduleActionType = 'notice' | 'no_action';

export interface FilingScheduleAdminMappingForm {
    stateReportTypeMappingId: number | null;
    typeOfReport: string;
    taxAmount: string;
    payTo: string;
    address: string;
    url: string;
    instructions: string;
    additionalNotes: string;
    actionType: FilingScheduleActionType;
    sameAsPremium: boolean;
    taxRequired: boolean;
    payToRequired: boolean;
    addressRequired: boolean;
    urlRequired: boolean;
    instructionsRequired: boolean;
    mapNotesRequired: boolean;
    /** Report or Invoice (single DB field Report_Invoice_Required_Y_N) */
    reqReportOrInvoice: boolean;
    reqRecon: boolean;
}

export interface FilingScheduleAdminPeriodForm {
    periodId: number | null;
    period: number;
    dueDate: string;
    timePeriod: string;
    label: string;
    /** When true, this period is excluded from State Report generation. */
    skip: boolean;
}

/**
 * Options "Skip" mirrors "all periods skipped". Used while the Options checkbox
 * remains in the UI pending product clarification.
 */
export function areAllPeriodsSkipped(
    periods: Array<{ skip?: boolean | null }>,
): boolean {
    return periods.length > 0 && periods.every((p) => !!p.skip);
}

/** Apply the Options Skip checkbox to every period (legacy schedule-wide control). */
export function applyOptionsSkipToPeriods<T extends { skip?: boolean }>(
    periods: T[],
    skip: boolean,
): T[] {
    return periods.map((p) => ({ ...p, skip }));
}

/** Persist Skip from the period row — not from the Options checkbox alone. */
export function mapPeriodSkipForSave(period: { skip?: boolean | null }): boolean {
    return !!period.skip;
}

export interface FilingScheduleAdminFormState {
    scheduleId: number;
    stateId: number;
    stateName: string;
    scheduleName: string;
    setDay: number;
    /** 1-12; drives the auto-calculated Annual due date month. Only used for Annual. */
    setMonth: number;
    dueDate: string;
    currentPeriodType: string;
    requestedPeriodType: string;
    periodTypePending: string | null;
    periodTypeEffDate: string | null;
    payment: boolean;
    report: boolean;
    paymentType: string;
    wetSignature: boolean;
    notaryStamp: boolean;
    zeroIndividual: boolean;
    zeroEntity: boolean;
    eom: boolean;
    skip: boolean;
    notes: string;
    reportingEffectiveDate: string;
    deactivationEffectiveDate: string;
    periodTypeEffectiveDate: string;
    disableZeroReport: boolean;
    periods: FilingScheduleAdminPeriodForm[];
    premiumMapping: FilingScheduleAdminMappingForm;
    zeroMapping: FilingScheduleAdminMappingForm;
}

export const PREMIUM_REPORT_TYPE = 'Premium-Bearing';
export const ZERO_REPORT_TYPE = 'Zero Report';

export function emptyMappingForm(typeOfReport: string): FilingScheduleAdminMappingForm {
    return {
        stateReportTypeMappingId: null,
        typeOfReport,
        taxAmount: '',
        payTo: '',
        address: '',
        url: '',
        instructions: '',
        additionalNotes: '',
        actionType: 'notice',
        sameAsPremium: false,
        taxRequired: false,
        payToRequired: false,
        addressRequired: false,
        urlRequired: false,
        instructionsRequired: false,
        mapNotesRequired: false,
        reqReportOrInvoice: false,
        reqRecon: false,
    };
}

export function isZeroReportType(typeOfReport: string | null | undefined): boolean {
    return String(typeOfReport || '').toLowerCase().includes('zero');
}

export function parseStateReportTaxAmount(value: unknown): number | null {
    const raw = String(value ?? '').replace(/[$,\s]/g, '').trim();
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
}

/**
 * True when the queue row is a zero report (vs premium). Tax amount > 0 => not zero.
 * Used with DisableZeroReport — do not apply schedule zero-disable rules to premium rows.
 */
export function isZeroQueueStateReportRow(row: {
    typeOfReport?: string | null;
    zeroIndividual?: boolean | string | number | null;
    zeroEntity?: boolean | string | number | null;
    taxAmount?: string | number | null;
}): boolean {
    if (isZeroReportType(row.typeOfReport)) return true;
    const tax = parseStateReportTaxAmount(row.taxAmount);
    if (tax !== null && tax > 0) return false;
    if (scheduleFlagToBoolean(row.zeroIndividual) || scheduleFlagToBoolean(row.zeroEntity)) {
        return true;
    }
    if (tax === 0) return true;
    return false;
}

/** True when schedule header has Disable Zero Report enabled (API may send bool or 'true'). */
export function isDisableZeroReportEnabled(value: unknown): boolean {
    if (value === true || value === 1) return true;
    const s = String(value ?? '').trim().toLowerCase();
    return s === '1' || s === 'true' || s === 'yes';
}

/** Field-level "Required" flags from filing schedule mapping (File Now + admin save). */
export interface FilingFieldRequirements {
    taxAmount: boolean;
    payTo: boolean;
    address: boolean;
    filingUrl: boolean;
    instructions: boolean;
    additionalNotes: boolean;
}

export const EMPTY_FILING_FIELD_REQUIREMENTS: FilingFieldRequirements = {
    taxAmount: false,
    payTo: false,
    address: false,
    filingUrl: false,
    instructions: false,
    additionalNotes: false,
};

export function scheduleFlagToBoolean(value: unknown): boolean {
    if (value === true || value === 1 || value === '1') return true;
    const s = String(value ?? '').trim().toLowerCase();
    return s === 'y' || s === 'yes' || s === 'true' || s === '1';
}

const MONTH_TIME_PERIOD_LABELS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];

const QUARTER_TIME_PERIOD_LABELS = [
    'January - March',
    'April - June',
    'July - September',
    'October - December',
];

const SEMI_ANNUAL_TIME_PERIOD_LABELS = [
    'January - June',
    'July - December',
];

/** Legacy shifted labels from old calculateTimePeriod() — replace on load/recalc. */
const LEGACY_SHIFTED_QUARTER_LABELS = [
    'October - December',
    'January - March',
    'April - June',
    'July - September',
];

const LEGACY_SHIFTED_SEMI_ANNUAL_LABELS = [
    'July - December',
    'January - June',
];

const LEGACY_ANNUAL_TIME_PERIOD_LABELS = [
    'Calendar Year',
];

/** Calendar coverage period for a schedule period number (Q1 = Jan–Mar, etc.). */
export function calculateTimePeriodLabel(
    periodType: string,
    periodNumber: number,
): string {
    const n = Math.max(1, Math.floor(periodNumber));
    if (periodType === 'Quarterly') {
        return QUARTER_TIME_PERIOD_LABELS[n - 1] || `Q${n}`;
    }
    if (periodType === 'Semi-Annual') {
        return SEMI_ANNUAL_TIME_PERIOD_LABELS[n - 1] || `H${n}`;
    }
    if (periodType === 'Annual') {
        return 'January - December';
    }
    if (periodType === 'Monthly') {
        return MONTH_TIME_PERIOD_LABELS[n - 1] || `M${n}`;
    }
    return '';
}

/** Prefer stored label unless empty, shorthand (Q1), or a known legacy shifted value. */
export function resolveTimePeriodLabel(
    periodType: string,
    periodNumber: number,
    rawTimePeriod?: string | null,
): string {
    const expected = calculateTimePeriodLabel(periodType, periodNumber);
    const trimmed = String(rawTimePeriod ?? '').trim();
    if (!trimmed || /^Q[1-4]$/i.test(trimmed) || /^H[12]$/i.test(trimmed)) {
        return expected;
    }
    if (periodType === 'Quarterly' && LEGACY_SHIFTED_QUARTER_LABELS.includes(trimmed)) {
        return expected;
    }
    if (
        periodType === 'Semi-Annual'
        && LEGACY_SHIFTED_SEMI_ANNUAL_LABELS.includes(trimmed)
    ) {
        return expected;
    }
    if (periodType === 'Annual' && LEGACY_ANNUAL_TIME_PERIOD_LABELS.includes(trimmed)) {
        return expected;
    }
    return trimmed;
}

/** TaxRequired / PayToRequired / etc. on the mapping — enforced on File Now only, not admin save. */
export function parseFilingFieldRequirementsFromSchedule(
    data: Record<string, unknown> | null | undefined,
): FilingFieldRequirements {
    if (!data) return { ...EMPTY_FILING_FIELD_REQUIREMENTS };
    return {
        taxAmount: scheduleFlagToBoolean(data.taxRequired ?? data.TaxRequired),
        payTo: scheduleFlagToBoolean(data.payToRequired ?? data.PayToRequired),
        address: scheduleFlagToBoolean(data.addressRequired ?? data.AddressRequired),
        filingUrl: scheduleFlagToBoolean(data.urlRequired ?? data.UrlRequired),
        instructions: scheduleFlagToBoolean(
            data.instructionsRequired ?? data.InstructionsRequired,
        ),
        additionalNotes: scheduleFlagToBoolean(
            data.additionalNotesRequired ?? data.AdditionalNotesRequired,
        ),
    };
}

function normalizeFileNowTextValue(value: unknown): string {
    return String(value ?? '').trim();
}

/** Treats blank and placeholder values (e.g. N/A) as empty for File Now form fields. */
export function normalizeFileNowFieldValue(value: unknown): string {
    const t = normalizeFileNowTextValue(value);
    return t.toUpperCase() === 'N/A' ? '' : t;
}

/** Empty or N/A → null for API/database. */
export function fileNowFieldToDbValue(value: unknown): string | null {
    const t = normalizeFileNowFieldValue(value);
    return t === '' ? null : t;
}

/** Strip currency formatting so "$0" and "0" count as present values. */
export function hasFileNowTaxAmountValue(value: unknown): boolean {
    return normalizeFileNowTextValue(value).replace(/[$,\s]/g, '') !== '';
}

/** Yup schema for File State Report — driven by admin Required toggles on the mapping. */
export function buildFileNowValidationSchema(requirements: FilingFieldRequirements) {
    const requiredString = (label: string) =>
        Yup.string().trim().required(`${label} is required`);

    return Yup.object({
        typeOfReport: Yup.string().trim().required('Type of Report is required'),
        taxAmount: requirements.taxAmount
            ? Yup.string().test(
                  'tax-required',
                  'Tax Amount is required',
                  (v) => hasFileNowTaxAmountValue(v),
              )
            : Yup.string(),
        payTo: requirements.payTo ? requiredString('Pay To') : Yup.string(),
        address: requirements.address
            ? Yup.string().test(
                  'address-required',
                  'Address is required',
                  (v) => normalizeFileNowFieldValue(v) !== '',
              )
            : Yup.string(),
        instructions: requirements.instructions
            ? requiredString('Instructions')
            : Yup.string(),
        filingUrl: requirements.filingUrl
            ? Yup.string().test(
                  'schedule-url',
                  'A filing URL must be configured on the schedule',
                  (v) => {
                      const t = normalizeFileNowTextValue(v);
                      return t !== '' && t.toUpperCase() !== 'N/A';
                  },
              )
            : Yup.string(),
        additionalNotes: requirements.additionalNotes
            ? requiredString('Additional Notes')
            : Yup.string(),
        paymentType: Yup.string(),
        largeReport: Yup.string().required('Large Report selection is required'),
    });
}

